package com.arena_web.ui.sections.feed.mobile

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.arena_web.data.dto.FeedItemDto
import com.arena_web.data.dto.FeedStoryAuthorGroupDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.icons.EMPTY_STATE_GLYPH_COLOR
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppTypography
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// Figma 8278:169490 — mobile Feed page content (stories tiles + post cards).
@Composable
fun FeedContentMobile(
    storyGroups: List<FeedStoryAuthorGroupDto>,
    items: List<FeedItemDto>,
    isLoadingMore: Boolean = false,
    onAuthorClick: (FeedItemDto) -> Unit = {},
    onLikeClick: (FeedItemDto) -> Unit = {},
    onDoubleTapLike: (FeedItemDto) -> Unit = {},
    onCommentClick: (FeedItemDto) -> Unit = {},
    onStoryClick: (FeedStoryAuthorGroupDto) -> Unit = {},
) {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .gap(24.px)
            .padding(top = 12.px)
    ) {
        if (storyGroups.isNotEmpty()) {
            MobileStoriesRow(
                groups = storyGroups,
                onAuthorClick = onStoryClick,
            )
        }

        Column(modifier = Modifier.fillMaxWidth().gap(12.px)) {
            if (items.isEmpty()) {
                Box(
                    modifier = Modifier.fillMaxWidth().minHeight(160.px).padding(top = 24.px),
                    contentAlignment = Alignment.Center
                ) {
                    FeedEmptyState()
                }
            } else {
                items.forEach { item ->
                    MobileFeedPost(
                        item = item,
                        onAuthorClick = { onAuthorClick(item) },
                        onLikeClick = { onLikeClick(item) },
                        onDoubleTapLike = { onDoubleTapLike(item) },
                        onCommentClick = { onCommentClick(item) },
                    )
                }
                if (isLoadingMore) {
                    Box(
                        modifier = Modifier.fillMaxWidth().padding(top = 16.px),
                        contentAlignment = Alignment.Center
                    ) {
                        SpanText(
                            t.feedLoadingMore,
                            modifier = Modifier.fontSize(13.px).color(AppColors.Text.Subtle)
                        )
                    }
                }
            }
        }

        // Bottom spacer — lifts the last post above the fixed BottomNavBar.
        // Padding-bottom on the scroll container is unreliable at the scroll
        // end on Chrome/Safari, so we emit a real sized Div instead.
        Div(
            attrs = {
                style {
                    property("width", "100%")
                    property("flex", "0 0 100px")
                    property("height", "100px")
                }
            }
        )
    }
}

// Empty feed placeholder — image-stack glyph (canonical empty-state colour) +
// prompt to follow players. Raw px to match the page.
@Composable
private fun FeedEmptyState() {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier.fillMaxWidth().padding(leftRight = 24.px).gap(16.px),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Box(
            modifier = Modifier.width(72.px).height(63.px).styleModifier {
                property("background-color", EMPTY_STATE_GLYPH_COLOR)
                property("-webkit-mask", "url('${AppIcons.EmptyImagesStack}') no-repeat center / contain")
                property("mask", "url('${AppIcons.EmptyImagesStack}') no-repeat center / contain")
            }
        )
        Column(
            modifier = Modifier.fillMaxWidth().gap(8.px),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            SpanText(
                t.feedEmptyTitle,
                modifier = Modifier
                    .fontSize(24.px)
                    .fontWeight(AppTypography.Medium)
                    .lineHeight(32.px)
                    .color(AppColors.Text.Secondary)
                    .textAlign(TextAlign.Center),
            )
            SpanText(
                t.feedEmptySubtitle,
                modifier = Modifier
                    .fillMaxWidth()
                    .fontSize(14.px)
                    .fontWeight(AppTypography.Regular)
                    .lineHeight(22.px)
                    .color(Color("#99B1BF"))
                    .textAlign(TextAlign.Center),
            )
        }
    }
}

// Figma 8278:169515 — Stories row.
// Four 80×80 tiles spread across the row with 4 px horizontal padding,
// each tile holds the author's last story preview behind a frosted blur
// with a mini player-card badge tucked into the bottom-left corner.
@Composable
private fun MobileStoriesRow(
    groups: List<FeedStoryAuthorGroupDto>,
    onAuthorClick: (FeedStoryAuthorGroupDto) -> Unit,
) {
    // Horizontally scrollable when there are more than 4 authors. With ≤ 4 we
    // align them across the row, matching the Figma justify-between layout.
    val many = groups.size > 4
    Div(attrs = {
        style {
            property("width", "100%")
            property("display", "flex")
            property("align-items", "center")
            property("padding", "0 4px")
            if (many) {
                property("gap", "12px")
                property("overflow-x", "auto")
                property("overflow-y", "hidden")
                property("scrollbar-width", "none")
            } else {
                property("justify-content", "space-between")
            }
        }
    }) {
        groups.forEach { group ->
            MobileStoryTile(group = group, onClick = { onAuthorClick(group) })
        }
    }
}

@Composable
private fun MobileStoryTile(
    group: FeedStoryAuthorGroupDto,
    onClick: () -> Unit,
) {
    val previewUrl = group.stories.lastOrNull()?.imageUrl

    Box(
        modifier = Modifier
            .size(80.px)
            .flexShrink(0)
            .cursor(Cursor.Pointer)
            .position(Position.Relative)
            .onClick { onClick() }
    ) {
        // Card image with the project gradient border (mask-composite trick:
        // the inner layers paint into padding-box, the gradient into
        // border-box, so the visible border becomes a slice of the gradient).
        Box(
            modifier = Modifier
                .fillMaxSize()
                .borderRadius(10.px)
                .overflow(Overflow.Hidden)
                .styleModifier {
                    property("border", "0.3px solid transparent")
                    val image = if (previewUrl != null) {
                        "url('$previewUrl') center/cover no-repeat padding-box"
                    } else {
                        "linear-gradient(0deg, #1A2238, #1A2238) padding-box"
                    }
                    property(
                        "background",
                        "$image, ${AppGradients.ButtonBorderGradientPrimary} border-box"
                    )
                }
        )

        // Frosted blur overlay — only for unseen story groups so seen ones
        // read as "all caught up" while teasing the unread previews. Figma
        // sizing kept: 80×80, slight (-0.5, -0.85) offset, 12 px radius.
        if (!group.allSeen) {
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .top((-0.85).px).left((-0.5).px)
                    .size(80.px)
                    .borderRadius(12.px)
                    .styleModifier {
                        property("background", "rgba(81,80,80,0.2)")
                        property("backdrop-filter", "blur(3.5px)")
                        property("-webkit-backdrop-filter", "blur(3.5px)")
                        property("pointer-events", "none")
                    }
            )
        }

        // Mini player card tucked into the bottom-left corner. The card
        // component is XtraSmall (58×90 design px → ~23×36 at mobile scale);
        // a CSS scale on the wrapper enlarges it visually without changing
        // the component or its internal layout. transform-origin keeps the
        // bottom-left anchor pinned to the offset position.
        Box(
            modifier = Modifier
                .position(Position.Absolute)
                .left((-4).px)
                .bottom((-4).px)
                .styleModifier {
                    property("pointer-events", "none")
                    property("transform", "scale(1.4)")
                    property("transform-origin", "bottom left")
                }
        ) {
            val cardFrameUrl = group.cardImageUrl
            if (cardFrameUrl != null) {
                PlayerCard(
                    playerName = "",
                    rating = 0,
                    position = "",
                    sport = "football",
                    size = PlayerCardSize.XtraSmall,
                    cardAssetsBasePath = group.cardAssetsBasePath,
                    cardTextColor = group.cardTextColor,
                    cardFrameUrl = cardFrameUrl,
                    avatarUrl = group.cardAvatarUrl ?: AppIcons.EmptyAvatar,
                    hideRating = true,
                    hideShadow = true,
                )
            }
        }
    }
}

// Figma 8278:169541 — Post card.
@Composable
private fun MobileFeedPost(
    item: FeedItemDto,
    onAuthorClick: () -> Unit = {},
    onLikeClick: () -> Unit = {},
    onDoubleTapLike: () -> Unit = {},
    onCommentClick: () -> Unit = {},
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .borderRadius(18.px)
            .padding(12.px)
            .styleModifier {
                property("border", "1px solid rgba(41,52,138,0.2)")
                // Figma gradient was -13.31deg (light at bottom → dark at
                // top); flipped to 166.69deg so the dark band sits at the top
                // and fades down to the lighter blue tint, matching the spec
                // the user signed off on.
                property(
                    "background",
                    "linear-gradient(166.69deg, rgba(52,119,250,0.05) 26.5%, rgba(2,4,11,0.5) 140%)"
                )
            }
            .gap(18.px)
    ) {
        // Header — avatar + meta + (text + image) stacked with 16 gap.
        Column(modifier = Modifier.fillMaxWidth().gap(16.px)) {
            Row(
                modifier = Modifier.fillMaxWidth().gap(12.px),
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Author avatar — mini player card for USER posts when card
                // data is available, round avatar fallback otherwise.
                val showPlayerCard = item.authorType == "USER" && item.cardImageUrl != null
                Box(
                    modifier = Modifier
                        .cursor(Cursor.Pointer)
                        .flexShrink(0)
                        .onClick { onAuthorClick() }
                ) {
                    if (showPlayerCard) {
                        PlayerCard(
                            playerName = "",
                            rating = 0,
                            position = "",
                            sport = "football",
                            size = PlayerCardSize.XtraSmall,
                            cardAssetsBasePath = item.cardAssetsBasePath,
                            cardTextColor = item.cardTextColor,
                            cardFrameUrl = item.cardImageUrl ?: "",
                            avatarUrl = item.cardAvatarUrl ?: AppIcons.EmptyAvatar,
                            hideRating = true,
                            hideShadow = true,
                        )
                    } else {
                        Box(
                            modifier = Modifier
                                .size(40.px)
                                .borderRadius(20.px)
                                .overflow(Overflow.Hidden)
                                .backgroundColor(Color("#1A2238")),
                            contentAlignment = Alignment.Center
                        ) {
                            if (item.authorAvatarUrl != null) {
                                Div(attrs = {
                                    ref { el ->
                                        el.innerHTML = "<img src=\"${item.authorAvatarUrl}\" " +
                                            "referrerpolicy=\"no-referrer\" " +
                                            "style=\"width:100%;height:100%;object-fit:cover;\" />"
                                        onDispose {}
                                    }
                                    style { property("width", "100%"); property("height", "100%") }
                                })
                            } else {
                                SpanText(
                                    item.authorName.take(1).uppercase(),
                                    modifier = Modifier
                                        .fontSize(16.px)
                                        .color(AppColors.Text.Primary)
                                )
                            }
                        }
                    }
                }

                // Name + date + location.
                Column(
                    modifier = Modifier
                        .flexGrow(1)
                        .styleModifier { property("min-width", "0") }
                        .gap(2.px)
                ) {
                    Row(
                        modifier = Modifier.fillMaxWidth().gap(8.px),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        SpanText(
                            item.authorName,
                            modifier = Modifier
                                .fontSize(16.px)
                                .fontWeight(500)
                                .lineHeight(18.px)
                                .color(AppColors.Text.Primary)
                                .styleModifier {
                                    property("white-space", "nowrap")
                                    property("overflow", "hidden")
                                    property("text-overflow", "ellipsis")
                                }
                        )
                        Row(
                            modifier = Modifier.gap(4.px).padding(left = 2.px),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Box(
                                modifier = Modifier
                                    .size(3.px)
                                    .borderRadius(50.percent)
                                    .backgroundColor(Color("#959697"))
                            )
                            SpanText(
                                formatFeedDate(item.createdAt),
                                modifier = Modifier
                                    .fontSize(14.px)
                                    .color(Color("#959697"))
                                    .styleModifier { property("white-space", "nowrap") }
                            )
                        }
                    }
                    if (!item.city.isNullOrBlank()) {
                        SpanText(
                            item.city!!,
                            modifier = Modifier
                                .fillMaxWidth()
                                .fontSize(14.px)
                                .color(Color("#B9B9B9"))
                                .styleModifier {
                                    property("overflow", "hidden")
                                    property("text-overflow", "ellipsis")
                                    property("white-space", "nowrap")
                                }
                        )
                    }
                }
            }

            // Post text + optional image.
            Column(
                modifier = Modifier.fillMaxWidth().padding(leftRight = 2.px).gap(14.px)
            ) {
                if (item.content.isNotBlank()) {
                    SpanText(
                        item.content,
                        modifier = Modifier
                            .fillMaxWidth()
                            .fontSize(15.px)
                            .color(Color("#BED6F5"))
                            .styleModifier {
                                property("line-height", "1.5")
                                property("white-space", "pre-wrap")
                                property("word-break", "break-word")
                            }
                    )
                }
                if (item.images.isNotEmpty()) {
                    var viewerIndex by remember(item.id) { mutableStateOf<Int?>(null) }
                    com.arena_web.ui.components.feed.DoubleTapLikeArea(
                        onDoubleTapLike = onDoubleTapLike,
                        modifier = Modifier.fillMaxWidth(),
                        onImageTap = { idx -> viewerIndex = idx },
                    ) {
                        MobilePostImageGrid(item.images.map { it.imageUrl }, item.layout)
                    }
                    viewerIndex?.let { idx ->
                        com.arena_web.ui.components.widgets.PhotoLightbox(
                            urls = item.images.map { it.imageUrl },
                            initialIndex = idx,
                            onDismiss = { viewerIndex = null },
                        )
                    }
                }
            }
        }

        // Likes + comments row.
        Row(
            modifier = Modifier.gap(18.px),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(
                modifier = Modifier.gap(8.px).cursor(Cursor.Pointer).onClick { onLikeClick() },
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(
                    src = AppIcons.Heart,
                    modifier = Modifier.size(22.px).styleModifier {
                        if (item.isLikedByMe) {
                            // Red tint for the liked state — matches the desktop card.
                            property(
                                "filter",
                                "brightness(0) saturate(100%) invert(27%) sepia(95%) " +
                                    "saturate(4997%) hue-rotate(344deg) brightness(95%) contrast(99%)"
                            )
                        } else {
                            property("filter", AppColors.IconDefaultFilter)
                        }
                    }
                )
                SpanText(
                    item.likesCount.toString(),
                    modifier = Modifier
                        .fontSize(16.px)
                        .color(Color("#BED6F5"))
                )
            }
            Row(
                modifier = Modifier.gap(8.px).cursor(Cursor.Pointer).onClick { onCommentClick() },
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(
                    src = AppIcons.Chat,
                    modifier = Modifier.size(20.px).styleModifier {
                        property("filter", AppColors.IconDefaultFilter)
                    }
                )
                SpanText(
                    item.commentsCount.toString(),
                    modifier = Modifier
                        .fontSize(16.px)
                        .color(Color("#BED6F5"))
                )
            }
        }
    }
}

// Mirrors the desktop FeedPostCard `PostImageGrid` layouts (1..6) at mobile
// dimensions. Same aspect ratios; raw 12 px gap + radius instead of scaled
// s() helpers so it reads correctly on the 390 px design baseline.
@Composable
private fun MobilePostImageGrid(urls: List<String>, layout: Int) {
    val gap = "12px"
    val radius = "12px"

    val slotFillMod = Modifier.flexGrow(1).fillMaxHeight().styleModifier {
        property("flex-basis", "0")
        property("min-width", "0")
    }
    val rowFillMod = Modifier.fillMaxWidth().flexGrow(1).styleModifier {
        property("flex-basis", "0")
        property("min-height", "0")
    }

    when (layout) {
        // 1 — single portrait, 3:4, centered at 75 % width.
        1 -> Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.Center
        ) {
            MobileFeedImage(
                urls[0], radius,
                Modifier.fillMaxWidth(75.percent).styleModifier { property("aspect-ratio", "3 / 4") }
            )
        }
        // 2 — single landscape, 16:9.
        2 -> MobileFeedImage(
            urls[0], radius,
            Modifier.fillMaxWidth().styleModifier { property("aspect-ratio", "16 / 9") }
        )
        // 3 — 2 portraits side-by-side, outer 4:3.
        3 -> Box(
            modifier = Modifier.fillMaxWidth().styleModifier { property("aspect-ratio", "4 / 3") }
        ) {
            Row(modifier = Modifier.fillMaxSize().styleModifier { property("gap", gap) }) {
                MobileFeedImage(urls[0], radius, slotFillMod)
                if (urls.size >= 2) MobileFeedImage(urls[1], radius, slotFillMod)
            }
        }
        // 4 — 2 landscape stacked, outer 4:3.
        4 -> Box(
            modifier = Modifier.fillMaxWidth().styleModifier { property("aspect-ratio", "4 / 3") }
        ) {
            Column(modifier = Modifier.fillMaxSize().styleModifier { property("gap", gap) }) {
                MobileFeedImage(urls[0], radius, rowFillMod)
                if (urls.size >= 2) MobileFeedImage(urls[1], radius, rowFillMod)
            }
        }
        // 5 — wide top + 2 below, outer 4:3 (rows 196:150).
        5 -> Box(
            modifier = Modifier.fillMaxWidth().styleModifier { property("aspect-ratio", "4 / 3") }
        ) {
            Column(modifier = Modifier.fillMaxSize().styleModifier { property("gap", gap) }) {
                MobileFeedImage(
                    urls[0], radius,
                    Modifier.fillMaxWidth().styleModifier {
                        property("flex", "196 0 0"); property("min-height", "0")
                    }
                )
                if (urls.size >= 2) Row(
                    modifier = Modifier.fillMaxWidth().styleModifier {
                        property("flex", "150 0 0"); property("min-height", "0"); property("gap", gap)
                    }
                ) {
                    MobileFeedImage(urls[1], radius, slotFillMod)
                    if (urls.size >= 3) MobileFeedImage(urls[2], radius, slotFillMod)
                }
            }
        }
        // 6 — 2 top + wide below, outer 4:3 (rows 150:196).
        6 -> Box(
            modifier = Modifier.fillMaxWidth().styleModifier { property("aspect-ratio", "4 / 3") }
        ) {
            Column(modifier = Modifier.fillMaxSize().styleModifier { property("gap", gap) }) {
                if (urls.size >= 2) Row(
                    modifier = Modifier.fillMaxWidth().styleModifier {
                        property("flex", "150 0 0"); property("min-height", "0"); property("gap", gap)
                    }
                ) {
                    MobileFeedImage(urls[0], radius, slotFillMod)
                    MobileFeedImage(urls[1], radius, slotFillMod)
                }
                if (urls.size >= 3) MobileFeedImage(
                    urls[2], radius,
                    Modifier.fillMaxWidth().styleModifier {
                        property("flex", "196 0 0"); property("min-height", "0")
                    }
                )
            }
        }
        else -> MobileFeedImage(
            urls[0], radius,
            Modifier.fillMaxWidth().styleModifier { property("aspect-ratio", "4 / 3") }
        )
    }
}

@Composable
private fun MobileFeedImage(url: String, radius: String, modifier: Modifier = Modifier) {
    Box(
        modifier = modifier.styleModifier {
            property("border-radius", radius)
            property("overflow", "hidden")
            property("background-image", "url('$url')")
            property("background-size", "cover")
            property("background-position", "center")
            property("background-repeat", "no-repeat")
        }
    )
}

// "19 Jun" style — short day + month abbreviation matching the Figma post header.
private fun formatFeedDate(iso: String?): String {
    if (iso.isNullOrBlank()) return ""
    return try {
        val d = kotlin.js.Date(iso)
        val day = d.getDate()
        val month = arrayOf(
            "Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        )[d.getMonth()]
        "$day $month"
    } catch (_: Throwable) {
        ""
    }
}
